import * as React from "react"
import { Head, Link } from "@inertiajs/react"
import { ArrowLeft, FileText, Menu, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ThemeToggle } from "@/components/ui/theme-toggle"
import { CopyMarkdownButton } from "@/components/docs/CopyMarkdownButton"
import {
  DocsSidebarNav,
  type DocsSection as NavSection,
} from "@/components/docs/DocsSidebarNav"

type Heading = {
  id: string
  text: string
  level: number
  raw_markdown_slice: string
}

type Section = {
  id: string
  title: string
  rendered_html: string
  raw_markdown: string
  headings: Heading[]
}

type Props = {
  sections: Section[]
  full_markdown: string
  generated_at: string
}

// Public API reference page at /docs/api. Mirrors /admin/design-system's
// layout: sticky header, responsive left sidebar with scroll-spy, single
// scrollable content column. The markdown source for every section is
// rendered server-side via Commonmarker so the whole document is present
// in the initial HTML — non-JS crawlers and external agents get the
// rendered output with no extra requests.
export default function DocsApi({ sections, full_markdown }: Props) {
  const [navOpen, setNavOpen] = React.useState(false)

  const navSections: NavSection[] = React.useMemo(
    () =>
      sections.map((s) => ({
        id: s.id,
        title: s.title,
        headings: s.headings.map((h) => ({
          id: h.id,
          text: h.text,
          level: h.level,
        })),
      })),
    [sections],
  )

  return (
    <>
      <Head title="SparkDrop API">
        <meta
          name="description"
          content="Full HTTP API reference for SparkDrop — endpoints, authentication, scheduling, comments, and the agent workflow contract."
        />
        <meta property="og:title" content="SparkDrop API" />
        <meta
          property="og:description"
          content="Full HTTP API reference for SparkDrop — for external agents, integrations, and operators."
        />
      </Head>

      <div className="min-h-screen bg-page text-ink-body">
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-hairline bg-page/80 px-4 backdrop-blur-sm sm:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setNavOpen(true)}
              className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-hairline bg-surface text-ink-body lg:hidden"
              aria-label="Open navigation"
            >
              <Menu className="h-4 w-4" />
            </button>
            <span className="font-display text-sm font-semibold text-ink-display">
              SparkDrop API
            </span>
            <span className="hidden text-xs text-ink-muted sm:inline">
              v1 reference
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild>
              <Link href="/" aria-label="Back to app">
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden md:inline">Back to app</span>
              </Link>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <a
                href="/docs/api.txt"
                aria-label="View entire reference as plain markdown (.txt)"
              >
                <FileText className="h-4 w-4" />
                <span className="hidden md:inline">View as .txt</span>
              </a>
            </Button>
            <CopyMarkdownButton
              markdown={full_markdown}
              label="Copy all as markdown"
              variant="compact"
            />
            <ThemeToggle />
          </div>
        </header>

        <div className="mx-auto flex max-w-7xl">
          <aside className="hidden w-64 shrink-0 border-r border-hairline lg:block">
            <div className="sticky top-14 max-h-[calc(100vh-3.5rem)] overflow-y-auto p-4">
              <DocsSidebarNav sections={navSections} />
            </div>
          </aside>

          {navOpen && (
            <div className="fixed inset-0 z-40 lg:hidden">
              <div
                className="absolute inset-0 bg-ink-display/40 backdrop-blur-sm"
                onClick={() => setNavOpen(false)}
              />
              <div className="absolute left-0 top-0 h-full w-72 overflow-y-auto bg-page p-4 shadow-xl">
                <div className="mb-4 flex items-center justify-between">
                  <span className="font-display text-sm font-semibold text-ink-display">
                    SparkDrop API
                  </span>
                  <button
                    type="button"
                    onClick={() => setNavOpen(false)}
                    className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-ink-muted hover:text-ink-display"
                    aria-label="Close navigation"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <DocsSidebarNav
                  sections={navSections}
                  onNavigate={() => setNavOpen(false)}
                />
              </div>
            </div>
          )}

          <main className="api-doc-content min-w-0 flex-1 px-4 py-8 sm:px-8 lg:px-12">
            {sections.map((section) => (
              <ApiDocSection key={section.id} section={section} />
            ))}
          </main>
        </div>
      </div>
    </>
  )
}

function ApiDocSection({ section }: { section: Section }) {
  // The rendered HTML already contains every heading with the right
  // anchor id. We render it verbatim; the per-section copy button sits
  // at the very top of the block, and per-h2 copy buttons sit
  // immediately after each h2 — injected via the floating-button layout
  // that follows the rendered HTML.
  return (
    <section id={section.id} className="api-doc-section scroll-mt-20">
      <div className="mb-4 flex items-center justify-between border-b border-hairline pb-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
          {section.title}
        </span>
        <CopyMarkdownButton
          markdown={section.raw_markdown}
          label="Copy section"
          variant="compact"
        />
      </div>

      <div
        className="api-doc-prose"
        // The HTML is generated server-side from a trusted hand-edited
        // markdown source. Commonmarker is invoked with `unsafe: false`,
        // GFM `tagfilter` enabled, and no raw HTML embedded in the .md
        // files — so this is the same risk profile as any other
        // server-rendered prose page.
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: section.rendered_html }}
      />

      <SubSectionCopyButtons section={section} />
    </section>
  )
}

// After the rendered HTML mounts in the DOM, attach a small "Copy"
// button next to each h2 anchor so an agent / human can copy just that
// endpoint's markdown without scrolling. We inject the buttons
// imperatively (rather than re-rendering the markdown via React) so the
// rendered HTML stays identical to what crawlers see — JS-required
// affordances live as a thin overlay on top.
function SubSectionCopyButtons({ section }: { section: Section }) {
  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (typeof document === "undefined") return

    const buttons = section.headings
      .filter((h) => h.level === 2)
      .map((heading) => ({
        heading,
        slice: heading.raw_markdown_slice,
      }))

    const cleanups = buttons
      .map(({ heading, slice }) => {
        const h2 = document.getElementById(heading.id)
        if (!h2) return null
        // h2 is the wrapping <h2>; commonmarker emits an inner <a> anchor
        // first, then the text. Append a small button to the h2's right
        // edge using a positioned wrapper.
        const button = renderCopyButton(slice, heading.text)
        h2.appendChild(button)
        return () => button.remove()
      })
      .filter(Boolean) as Array<() => void>

    return () => cleanups.forEach((fn) => fn())
  }, [section])

  return <div ref={containerRef} aria-hidden="true" />
}

function renderCopyButton(markdown: string, headingText: string) {
  const wrapper = document.createElement("button")
  wrapper.type = "button"
  wrapper.setAttribute("aria-label", `Copy "${headingText}" section as markdown`)
  wrapper.className =
    "api-doc-copy-h2 ml-2 inline-flex cursor-pointer items-center gap-1 rounded-md border border-hairline bg-surface px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-ink-muted opacity-0 transition-opacity hover:bg-page hover:text-ink-display"
  wrapper.textContent = "Copy"
  wrapper.addEventListener("click", async (event) => {
    event.preventDefault()
    event.stopPropagation()
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(markdown)
      } else {
        const ta = document.createElement("textarea")
        ta.value = markdown
        document.body.appendChild(ta)
        ta.select()
        try { document.execCommand("copy") } catch { /* noop */ }
        document.body.removeChild(ta)
      }
      const original = wrapper.textContent
      wrapper.textContent = "Copied!"
      setTimeout(() => {
        wrapper.textContent = original
      }, 1500)
    } catch {
      // No-op — clipboard unavailable.
    }
  })
  return wrapper
}
